import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { deleteCompany, listCompanyRecords, syncCompaniesFromApi, type CompanyRecord } from "@/lib/admin/companiesStore";
import { companyFormErrorMessage } from "@/lib/admin/companyForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  Building2,
  CalendarIcon,
  Edit,
  Eye,
  Filter,
  Globe,
  LayoutGrid,
  Link2,
  List,
  Mail,
  MapPin,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type CoSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "status_active_first" | "status_inactive_first";

const Companies = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "company_can_view");
  const canCreate = can(user, "company_can_create");
  const canUpdate = can(user, "company_can_update");
  const canDelete = can(user, "company_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const rows = useMemo(() => listCompanyRecords(), [version]);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<CoSortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterSlugQuery, setFilterSlugQuery] = useState("");
  const [filterDomain, setFilterDomain] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [deleteTarget, setDeleteTarget] = useState<CompanyRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsForbidden(false);
      try {
        await syncCompaniesFromApi();
        if (mounted) refresh();
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error("[Companies] Load failed", e);
        if (mounted) toast.error(companyFormErrorMessage(e) || "Could not fetch companies. Showing cached data.");
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameN = filterName.trim().toLowerCase();
    const slugQ = filterSlugQuery.trim().toLowerCase();
    const domQ = filterDomain.trim().toLowerCase();

    return rows.filter((c) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(c.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(c.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (q) {
        const blob = [c.name, c.slug, c.domain, c.email, c.phone, c.address].map((s) => (s ?? "").toLowerCase());
        if (!blob.some((x) => x.includes(q))) return false;
      }
      if (nameN && !c.name.toLowerCase().includes(nameN)) return false;
      if (slugQ && !c.slug.toLowerCase().includes(slugQ)) return false;
      if (domQ && !(c.domain ?? "").toLowerCase().includes(domQ)) return false;
      if (onlyActive && c.status !== "active") return false;
      if (onlyInactive && c.status !== "inactive") return false;
      return true;
    });
  }, [
    rows,
    query,
    filterCreatedDate,
    quickDate,
    filterName,
    filterSlugQuery,
    filterDomain,
    onlyActive,
    onlyInactive,
  ]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "status_active_first": {
          const sa = a.status === "active" ? 0 : 1;
          const sb = b.status === "active" ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return a.name.localeCompare(b.name);
        }
        case "status_inactive_first": {
          const sa = a.status === "inactive" ? 0 : 1;
          const sb = b.status === "inactive" ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return a.name.localeCompare(b.name);
        }
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterSlugQuery.trim() ? 1 : 0) +
    (filterDomain.trim() ? 1 : 0) +
    (onlyActive ? 1 : 0) +
    (onlyInactive ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterSlugQuery("");
    setFilterDomain("");
    setOnlyActive(false);
    setOnlyInactive(false);
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterSlugQuery}|${filterDomain}|${onlyActive ? 1 : 0}|${onlyInactive ? 1 : 0}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const confirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const r = await deleteCompany(deleteTarget.id);
      setDeleteTarget(null);
      refresh();
      toast.success(r.message);
    } catch (e) {
      console.error("[Companies] Delete failed", e);
      const msg = companyFormErrorMessage(e);
      if (isAxiosError(e) && e.response?.status === 422) {
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const statusBadge = (c: CompanyRecord) =>
    c.status === "active"
      ? { label: "Active", className: "bg-emerald-500/15 text-emerald-600" }
      : { label: "Inactive", className: "bg-slate-500/15 text-slate-500" };

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Companies access required"
        message="You don’t have permission to view companies. Ask an administrator to grant company_can_view_any."
      />
    );
  }

  return (
    <Layout title="Companies">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Companies</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Search, filter, and manage tenant profiles.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" asChild>
              <Link to="/companies/add">
                <Plus className="w-4 h-4" /> Add company
              </Link>
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, domain, email, phone, address…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter companies</SheetTitle>
                  <SheetDescription>Refine by name, slug, domain, status, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Identity</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Slug contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterSlugQuery}
                        onChange={(e) => setFilterSlugQuery(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Domain contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterDomain}
                        onChange={(e) => setFilterDomain(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar
                            mode="single"
                            selected={filterCreatedDate}
                            onSelect={setFilterCreatedDate}
                            initialFocus
                          />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Active only</Label>
                      <p className="text-xs text-muted-foreground">Status = active</p>
                    </div>
                    <Checkbox
                      checked={onlyActive}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyActive(next);
                        if (next) setOnlyInactive(false);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Inactive only</Label>
                      <p className="text-xs text-muted-foreground">Status = inactive</p>
                    </div>
                    <Checkbox
                      checked={onlyInactive}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyInactive(next);
                        if (next) setOnlyActive(false);
                      }}
                    />
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="companies-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="w-[180px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as CoSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="status_active_first">Active first</SelectItem>
                  <SelectItem value="status_inactive_first">Inactive first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="companies-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as CoSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="status_active_first">Active first</SelectItem>
                    <SelectItem value="status_inactive_first">Inactive first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0 ? (
              canCreate ? (
                <>
                  No companies yet.{" "}
                  <Link to="/companies/add" className="text-primary font-medium hover:underline">
                    Add a company
                  </Link>{" "}
                  (same layout as Add Employee).
                </>
              ) : (
                "No companies yet."
              )
            ) : (
              "No companies match your search or filters."
            )}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={c.name}
                  titleIcon={Building2}
                  statusBadge={statusBadge(c)}
                  primaryMeta={{
                    label: "Slug",
                    value: c.slug,
                    icon: Link2,
                    iconClassName: "text-emerald-600",
                  }}
                  secondaryMeta={{
                    label: c.domain ? "Domain" : c.email ? "Email" : "Address",
                    value: c.domain ?? c.email ?? c.phone ?? c.address?.slice(0, 80) ?? "—",
                    icon: c.domain ? Globe : c.email ? Mail : c.address ? MapPin : Building2,
                    iconClassName: c.domain ? "text-sky-600" : c.email ? "text-amber-600" : "text-red-600",
                  }}
                  footer={`Last updated: ${formatCatalogAuditDate(c.updatedAt)}`}
                  avatarUrl={c.logo}
                  onView={canView ? () => navigate(`/companies/${c.id}`) : undefined}
                  onEdit={canUpdate ? () => navigate(`/companies/${c.id}/edit`) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(c) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    {c.logo ? (
                      <img src={c.logo} alt="" className="h-full w-full object-contain" />
                    ) : (
                      <Building2 className="w-8 h-8 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{c.name}</h3>
                      <span
                        className={cn(
                          "text-[11px] px-2 py-0.5 rounded-full font-medium font-mono truncate max-w-[160px]",
                          c.status === "active" ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-500/15 text-slate-500",
                        )}
                      >
                        {c.slug}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {c.domain ?? c.email ?? "No domain or email on file"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(c.updatedAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="h-8 px-2 text-xs" asChild>
                        <Link to={`/companies/${c.id}`} className="inline-flex items-center gap-1.5">
                          <Eye className="w-3 h-3 shrink-0" /> View
                        </Link>
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="h-8 px-2 text-xs" asChild>
                        <Link to={`/companies/${c.id}/edit`} className="inline-flex items-center gap-1.5">
                          <Edit className="w-3 h-3 shrink-0" /> Edit
                        </Link>
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 h-8 w-8 p-0"
                        onClick={() => setDeleteTarget(c)}
                        aria-label="Delete company"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this company?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete <strong className="text-foreground font-semibold">{deleteTarget.name}</strong>? It must not be linked
                to any user accounts. This cannot be undone.
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
};

export default Companies;
